import statuses from '@/helpers/statuses'

const initialState = () => ({
  kudos: {},
  list: [],
  userKudos: {},
  teamKudos: {},
  proficient: {},
  meta: {
    loading: statuses.IDLE,
    userLoading: statuses.IDLE,
    teamLoading: statuses.IDLE,
    proficientLoading: {}
  }
})

export default api => ({
  state: initialState,

  getters: {
    kudosLoading: state => state.meta.loading,
    isKudosLoading: state => state.meta.loading === statuses.LOADING,
    kudos: state => state.list.map(id => state.kudos[id]),

    allUserKudos: state => state.userKudos,
    userKudosLoading: state => state.meta.userLoading,
    isUserKudosLoading: state => state.meta.userLoading === statuses.LOADING,
    meKudos: (state, getters) => state.userKudos[getters.profileMe.id],
    meKudosReceived: (state, getters) => () => (state.userKudos[getters.profileMe.id] || {}).received || [],
    userKudos: state => id => state.userKudos[id],
    userKudosReceived: state => id => (state.userKudos[id] || {}).received || [],

    teamKudosLoading: state => state.meta.teamLoading,
    isTeamKudosLoading: state => state.meta.teamLoading === statuses.LOADING,
    teamKudos: state => id => state.teamKudos[id],
    teamKudosReceived: state => id => (state.teamKudos[id] || {}).received || [],

    kudoProficientLoading: state => kudoId => state.meta.proficientLoading[kudoId] || statuses.IDLE,
    isKudoProficientLoading: (state, getters) => kudoId => getters.kudoProficientLoading(kudoId) === statuses.LOADING,
    kudoProficient: state => kudoId => state.proficient[kudoId]
  },

  mutations: {
    fetchKudos: state => {
      state.meta.loading = statuses.LOADING
    },

    fetchKudosSuccess: (state, { kudos, list }) => {
      state.meta.loading = statuses.LOADED
      state.kudos = kudos
      state.list = list
    },

    fetchKudosFailure: (state, error) => {
      state.meta.loading = statuses.FAILED
    },

    fetchUserKudos: state => {
      state.meta.userLoading = statuses.LOADING
    },

    fetchUserKudosSuccess: (state, { id, kudos }) => {
      state.meta.userLoading = statuses.LOADED
      state.userKudos = { ...state.userKudos, [id]: kudos }
    },

    fetchUserKudosFailure: (state, error) => {
      state.meta.userLoading = statuses.FAILED
    },

    fetchTeamKudos: state => {
      state.meta.teamLoading = statuses.LOADING
    },

    fetchTeamKudosSuccess: (state, { id, kudos }) => {
      state.meta.teamLoading = statuses.LOADED
      state.teamKudos = { ...state.teamKudos, [id]: kudos }
    },

    fetchTeamKudosFailure: (state, error) => {
      state.meta.teamLoading = statuses.FAILED
    },

    fetchKudoProficient: (state, kudoId) => {
      state.meta.proficientLoading = { ...state.meta.proficientLoading, [kudoId]: statuses.LOADING }
    },

    fetchKudoProficientSuccess: (state, { kudoId, data }) => {
      state.meta.proficientLoading = { ...state.meta.proficientLoading, [kudoId]: statuses.LOADED }
      state.proficient = { ...state.proficient, [kudoId]: data }
    },

    fetchKudoProficientFailure: (state, { kudoId, error }) => {
      state.meta.proficientLoading = { ...state.meta.proficientLoading, [kudoId]: statuses.FAILED }
    },

    resetKudos: state => {
      const payload = initialState()
      Object.keys(payload).forEach(key => {
        state[key] = payload[key]
      })
    }
  },

  actions: {
    fetchKudos: async ({ commit }) => {
      commit('fetchKudos')
      try {
        const { entities: { kudos }, result } = await api.fetchKudos()
        commit('fetchKudosSuccess', { kudos, list: result })
      } catch (error) {
        commit('fetchKudosFailure', error)
      }
    },

    fetchKudosIfNot: async ({ getters, dispatch }) => {
      if (getters.kudosLoading !== statuses.LOADED) {
        await dispatch('fetchKudos')
      }
    },

    fetchUserKudos: async ({ commit }, profileId) => {
      commit('fetchUserKudos')
      try {
        const kudos = await api.fetchUserKudos(profileId)
        commit('fetchUserKudosSuccess', { id: profileId, kudos })
      } catch (error) {
        commit('fetchUserKudosFailure', error)
      }
    },

    fetchMeKudos: async ({ commit, getters }) => {
      commit('fetchUserKudos')
      try {
        const kudos = await api.fetchUserMeKudos()
        const { id } = getters.profileMe
        commit('fetchUserKudosSuccess', { id, kudos })
      } catch (error) {
        commit('fetchUserKudosFailure', error)
      }
    },

    fetchTeamKudos: async ({ commit }, teamId) => {
      const slug = api.getWorkspaceSlug()
      commit('fetchTeamKudos')
      try {
        const kudos = await api.fetchTeamKudos(slug, teamId)
        commit('fetchTeamKudosSuccess', { id: teamId, kudos })
      } catch (error) {
        commit('fetchTeamKudosFailure', error)
      }
    },

    fetchKudoProficient: async ({ commit }, kudoId) => {
      const slug = api.getWorkspaceSlug()
      commit('fetchKudoProficient', kudoId)
      try {
        const data = await api.fetchKudoProficient(slug, kudoId)
        commit('fetchKudoProficientSuccess', { kudoId, data })
      } catch (error) {
        commit('fetchKudoProficientFailure', { kudoId, error })
      }
    },

    fetchKudoProficientIfNot: async ({ getters, dispatch }, kudoId) => {
      if (getters.kudoProficientLoading(kudoId) !== statuses.LOADED) {
        await dispatch('fetchKudoProficient', kudoId)
      }
    },

    sendKudo: async ({ commit }, data) => {
      const slug = api.getWorkspaceSlug()
      await api.sendKudo(slug, data)
    },

    kudoRecognition: async ({ commit }, key) => {
      return await api.kudoRecognition(key)
    },

    sendKudoRecognition: async ({ commit }, { key, data }) => {
      return await api.sendKudoRecognition(key, data)
    },

    recognitionInvite: async ({ commit }, data) => {
      const slug = api.getWorkspaceSlug()
      await api.recognitionInvite(slug, data)
    },

    clear: ({ commit }) => {
      commit('resetKudos')
    }
  }
})
